import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { CTABanner } from "@/components/site/CTABanner";
import { CATEGORIES, CHAPTERS, FIRM, OUTCOME_ROWS, SERVICES } from "@/data/content";

const HERO_IMG =
    "https://images.unsplash.com/photo-1693853121291-ff68d16e3823?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHszfHxkZXNlcnQlMjBhcmNoaXRlY3R1cmFsJTIwbW9kZXJuJTIwY29uY3JldGUlMjB0dWNzb24lMjBtb3VudGFpbnMlMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4ODcyMzgxNHww&ixlib=rb-4.1.0&q=85";

const HeroLine = ({ children, delay, reduce }) => (
    <span className="block overflow-hidden pb-[0.16em] -mb-[0.16em]">
        <motion.span
            className="block"
            initial={reduce ? false : { y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

    return (
        <section ref={ref} data-testid="home-hero" className="relative flex min-h-[100svh] items-end overflow-hidden">
            <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
                <img
                    src={HERO_IMG}
                    alt="Sonoran desert landscape at dusk with distant mountain ridgelines near Tucson, Arizona"
                    className="h-full w-full object-cover"
                    loading="eager"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#070a0f] via-[#070a0f]/70 to-[#070a0f]/40" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070a0f]/80 via-transparent to-transparent" />

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-6 lg:px-12">
                <motion.p
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400"
                >
                    {FIRM.hq} · In Business Since 2004
                </motion.p>
                <motion.div
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="mt-8"
                >
                    <Marquee overlay />
                </motion.div>
                <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tight text-slate-50 sm:text-7xl lg:text-8xl">
                    <HeroLine delay={0.35} reduce={reduce}>Executive consulting</HeroLine>
                    <HeroLine delay={0.5} reduce={reduce}>
                        for the <em className="text-slate-300">receivables</em>
                    </HeroLine>
                    <HeroLine delay={0.65} reduce={reduce}>industry.</HeroLine>
                </h1>
                <motion.p
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                >
                    {FIRM.name} is a principal-led consulting practice serving leaders across the
                    receivables industry, combining decades of experience with strategic insight,
                    market intelligence, executive advisory, and emerging AI capabilities.
                </motion.p>
                <motion.div
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/services"
                        data-testid="hero-cta-services"
                        className="group inline-flex items-center gap-3 bg-slate-100 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-900 transition-colors duration-300 hover:bg-white"
                    >
                        Explore the Practice
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="hero-cta-contact"
                        className="inline-flex items-center gap-3 border border-slate-600 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-200 transition-colors duration-300 hover:border-slate-300 hover:text-white"
                    >
                        Confidential Inquiry
                    </Link>
                </motion.div>
            </motion.div>

            <motion.div
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.3 }}
                className="absolute bottom-8 right-4 z-10 hidden items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 sm:right-6 md:flex lg:right-12"
            >
                <span>{FIRM.coordinates}</span>
                <span className="h-px w-10 bg-slate-700" />
                <ArrowDown size={12} className="animate-bounce" />
            </motion.div>
        </section>
    );
};

const MissionStrip = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0" aria-hidden="true">
            <img src="https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=1600&q=80" alt="" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-[#070a0f]/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070a0f]/95 via-[#070a0f]/80 to-[#070a0f]/55" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
            <Reveal className="lg:col-span-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">The Practice</p>
                <p className="mt-6 font-serif text-2xl font-medium leading-[1.35] tracking-tight text-slate-200 sm:text-3xl lg:text-4xl">
                    We advise the executives who carry the compliance, performance, and
                    reputation of a collection operation, quietly, independently, and to a
                    standard that withstands scrutiny. A boutique practice by design, built to scale
                    selectively with senior subject-matter experts when the mandate demands it.
                </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-4">
                <dl className="divide-y divide-amber-500/40 border border-amber-500/70 px-6">
                    {[
                        ["2004", "In business, serving the industry since"],
                        ["30+", "Years of receivables industry leadership"],
                        ["14", "Advisory specialties across four domains"],
                        ["AZ + US", "Arizona-rooted, nationally engaged"],
                    ].map(([v, l]) => (
                        <div key={l} className="flex items-baseline justify-between gap-6 py-5">
                            <dt className="font-mono text-2xl text-slate-100">{v}</dt>
                            <dd className="max-w-[12rem] text-right text-xs leading-relaxed text-slate-500">{l}</dd>
                        </div>
                    ))}
                </dl>
            </Reveal>
        </div>
    </section>
);

const PortfolioPreview = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0" aria-hidden="true">
            <img src="https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1600&q=80" alt="" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-[#070a0f]/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070a0f]/95 via-[#070a0f]/80 to-[#070a0f]/55" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Portfolio</p>
                        <h2 className="mt-5 max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                            Fourteen specialties. Four domains. One standard.
                        </h2>
                    </div>
                    <Link
                        to="/services"
                        data-testid="portfolio-view-all-link"
                        className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:text-white"
                    >
                        All Services
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {CATEGORIES.map((cat, i) => {
                    const count = SERVICES.filter((s) => s.category === cat).length;
                    return (
                        <Reveal key={cat} delay={i * 0.08} className="h-full">
                            <Link
                                to="/services"
                                data-testid={`portfolio-category-card-${i + 1}`}
                                className="group flex h-full flex-col justify-between border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]"
                            >
                                <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500">
                                    0{i + 1} / {String(count).padStart(2, "0")} Specialties
                                </span>
                                <span className="mt-16 font-serif text-2xl font-medium leading-snug text-slate-200 transition-colors duration-300 group-hover:text-white sm:text-3xl">
                                    {cat}
                                </span>
                                <span className="mt-8 h-px w-full bg-slate-800 transition-colors duration-300 group-hover:bg-amber-500/70" />
                            </Link>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const Manifesto = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0" aria-hidden="true">
            <img src="https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1600&q=80" alt="" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-[#070a0f]/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070a0f]/95 via-[#070a0f]/80 to-[#070a0f]/55" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Operating Principles</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    A vision in four chapters.
                </h2>
            </Reveal>
            <div className="mt-16 space-y-6">
                {CHAPTERS.map((c, i) => (
                    <Reveal key={c.num} delay={i * 0.05}>
                        <div
                            data-testid={`manifesto-chapter-${c.num}`}
                            className="group grid gap-4 border border-amber-500/70 bg-[#0b0f16]/60 py-10 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d] sm:grid-cols-12 sm:gap-8 sm:px-6 lg:py-14"
                        >
                            <span className="font-mono text-sm tracking-[0.3em] text-amber-500 transition-colors duration-300 group-hover:text-amber-400 sm:col-span-2">
                                CH. {c.num}
                            </span>
                            <h3 className="font-serif text-2xl font-medium tracking-tight text-slate-100 sm:col-span-4 sm:text-3xl lg:text-4xl">
                                {c.title}
                            </h3>
                            <p className="max-w-xl text-sm leading-relaxed text-slate-400 sm:col-span-6 sm:text-base">
                                {c.body}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const OutcomeIndex = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0" aria-hidden="true">
            <img src="https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1600&q=80" alt="" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-[#070a0f]/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070a0f]/95 via-[#070a0f]/80 to-[#070a0f]/55" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
            <Reveal className="lg:col-span-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Outcome Index</p>
                <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                    What an engagement is built to change.
                </h2>
                <p className="mt-6 text-sm leading-relaxed text-slate-400">
                    We do not measure success in reports delivered. We measure it in the
                    institutional capacities that remain after we leave.
                </p>
            </Reveal>
            <div className="space-y-4 lg:col-span-8">
                {OUTCOME_ROWS.map((row, i) => (
                    <Reveal key={row.index} delay={i * 0.05}>
                        <div
                            data-testid={`outcome-row-${i + 1}`}
                            className="group grid grid-cols-12 items-baseline gap-4 border border-amber-500/70 bg-[#0b0f16]/60 px-5 py-5 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]"
                        >
                            <span className="col-span-3 font-mono text-[11px] tracking-[0.25em] text-amber-500 sm:col-span-2">
                                {row.index}
                            </span>
                            <span className="col-span-9 font-serif text-lg text-slate-200 transition-colors duration-300 group-hover:text-white sm:col-span-4 sm:text-xl">
                                {row.label}
                            </span>
                            <span className="col-span-12 text-xs leading-relaxed text-slate-500 sm:col-span-6">
                                {row.note}
                            </span>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <PageMeta
                title="Nationwide Strategies LLC | Collection Agency Consulting | Tucson, Arizona"
                description="Principal-led consulting for collection agency and receivables industry leaders. Compliance, operations, leadership development, and strategic consulting from Tucson, Arizona."
            />
            <Hero />
            <MissionStrip />
            <PortfolioPreview />
            <Manifesto />
            <OutcomeIndex />
            <CTABanner />
        </>
    );
}
